const DESKTOP_INTERACTION_BREAKPOINT = 1034;

export const isDesktopInteraction = (width) => width >= DESKTOP_INTERACTION_BREAKPOINT;

const initializedPurposeSections = new WeakSet();
const initializedImpactSections = new WeakSet();
const initializedHintSections = new WeakSet();

const getScopedElements = (scope, selector) => {
  if (scope.matches?.(selector)) {
    return [scope];
  }

  return scope.querySelectorAll?.(selector) ?? [];
};

const initCharityPurposeInteractions = (scope = document) => {
  const sections = getScopedElements(scope, '.charity-purpose-sec');

  sections.forEach((section) => {
    if (initializedPurposeSections.has(section)) {
      return;
    }

    const triggers = Array.from(section.querySelectorAll('[data-charity-focus-trigger]'));

    if (!triggers.length) {
      return;
    }

    initializedPurposeSections.add(section);

    let resizeFrame = null;

    const setActive = (trigger, isActive) => {
      const extra = trigger.querySelector('[data-charity-focus-extra]');

      trigger.classList.toggle('is-extra-active', isActive);
      trigger.setAttribute('aria-expanded', String(isActive));

      if (extra) {
        extra.setAttribute('aria-hidden', String(!isActive));
      }
    };

    const closeOthers = (activeTrigger) => {
      triggers.forEach((trigger) => {
        if (trigger !== activeTrigger) {
          setActive(trigger, false);
        }
      });
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('mouseenter', () => {
        if (isDesktopInteraction(window.innerWidth)) {
          setActive(trigger, true);
        }
      });

      trigger.addEventListener('mouseleave', () => {
        if (isDesktopInteraction(window.innerWidth)) {
          setActive(trigger, false);
        }
      });

      trigger.addEventListener('focusin', () => {
        if (isDesktopInteraction(window.innerWidth)) {
          setActive(trigger, true);
        }
      });

      trigger.addEventListener('focusout', () => {
        if (isDesktopInteraction(window.innerWidth)) {
          setActive(trigger, false);
        }
      });

      trigger.addEventListener('click', () => {
        if (isDesktopInteraction(window.innerWidth)) {
          return;
        }

        const willOpen = !trigger.classList.contains('is-extra-active');

        closeOthers(trigger);
        setActive(trigger, willOpen);
      });
    });

    window.addEventListener('resize', () => {
      if (resizeFrame) {
        return;
      }

      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        closeOthers(null);
      });
    }, { passive: true });
  });
};

const initCharityImpactInteractions = (scope = document) => {
  const sections = getScopedElements(scope, '.charity-impact-sec');

  sections.forEach((section) => {
    if (initializedImpactSections.has(section)) {
      return;
    }

    const triggers = Array.from(section.querySelectorAll('[data-charity-impact-trigger]'));

    if (!triggers.length) {
      return;
    }

    initializedImpactSections.add(section);

    let resizeFrame = null;

    const setPopoverActive = (trigger, isActive) => {
      const card = trigger.closest('.charity-impact-sec__card');
      const popover = card?.querySelector('[data-charity-impact-popover]');

      card?.classList.toggle('is-popover-open', isActive);
      trigger.setAttribute('aria-expanded', String(isActive));

      if (popover) {
        popover.setAttribute('aria-hidden', String(!isActive));
      }
    };

    const closePopovers = () => {
      triggers.forEach((trigger) => setPopoverActive(trigger, false));
    };

    triggers.forEach((trigger) => {
      const card = trigger.closest('.charity-impact-sec__card');
      let pointerStart = null;
      let skipNextClick = false;

      if (!card) {
        return;
      }

      const togglePopover = () => {
        const willOpen = !card.classList.contains('is-popover-open');

        closePopovers();
        setPopoverActive(trigger, willOpen);
      };

      card.addEventListener('mouseenter', () => {
        if (isDesktopInteraction(window.innerWidth)) {
          closePopovers();
          setPopoverActive(trigger, true);
        }
      });

      card.addEventListener('mouseleave', () => {
        if (isDesktopInteraction(window.innerWidth)) {
          setPopoverActive(trigger, false);
        }
      });

      trigger.addEventListener('focusin', () => {
        if (isDesktopInteraction(window.innerWidth)) {
          closePopovers();
          setPopoverActive(trigger, true);
        }
      });

      trigger.addEventListener('focusout', () => {
        if (isDesktopInteraction(window.innerWidth)) {
          setPopoverActive(trigger, false);
        }
      });

      trigger.addEventListener('pointerdown', (event) => {
        if (isDesktopInteraction(window.innerWidth) || !event.isPrimary) {
          return;
        }

        pointerStart = {
          id: event.pointerId,
          x: event.clientX,
          y: event.clientY,
        };
      });

      trigger.addEventListener('pointerup', (event) => {
        if (isDesktopInteraction(window.innerWidth) || !pointerStart || event.pointerId !== pointerStart.id) {
          return;
        }

        const distance = Math.hypot(event.clientX - pointerStart.x, event.clientY - pointerStart.y);

        pointerStart = null;

        if (distance > 12) {
          return;
        }

        togglePopover();
        skipNextClick = true;
        window.setTimeout(() => {
          skipNextClick = false;
        }, 500);
      });

      trigger.addEventListener('pointercancel', () => {
        pointerStart = null;
      });

      trigger.addEventListener('click', () => {
        if (isDesktopInteraction(window.innerWidth)) {
          return;
        }

        if (skipNextClick) {
          return;
        }

        togglePopover();
      });
    });

    window.addEventListener('resize', () => {
      if (resizeFrame) {
        return;
      }

      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        closePopovers();
      });
    }, { passive: true });
  });
};

const initCharityInteractionHints = (scope = document) => {
  const hintDefinitions = [
    {
      section: '.charity-purpose-sec',
      card: '.charity-purpose-sec__focus-card',
      trigger: '[data-charity-focus-trigger]',
    },
    {
      section: '.charity-impact-sec',
      card: '.charity-impact-sec__card',
      trigger: '[data-charity-impact-trigger]',
    },
  ];

  hintDefinitions.forEach(({ section: sectionSelector, card: cardSelector, trigger: triggerSelector }) => {
    getScopedElements(scope, sectionSelector).forEach((section) => {
      if (initializedHintSections.has(section)) {
        return;
      }

      const card = section.querySelector(cardSelector);
      const trigger = card?.querySelector(triggerSelector);

      if (!card || !trigger) {
        return;
      }

      initializedHintSections.add(section);

      let timeoutId = null;
      let observer = null;

      const dismissHint = () => {
        card.classList.remove('is-interaction-hint');

        if (timeoutId) {
          window.clearTimeout(timeoutId);
          timeoutId = null;
        }

        observer?.unobserve(card);
      };

      const showHint = () => {
        if (isDesktopInteraction(window.innerWidth)) {
          return;
        }

        card.classList.add('is-interaction-hint');
        timeoutId = window.setTimeout(dismissHint, 2800);
        observer?.unobserve(card);
      };

      trigger.addEventListener('pointerdown', dismissHint, { passive: true, once: true });

      if (!('IntersectionObserver' in window)) {
        showHint();
        return;
      }

      observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            showHint();
          }
        });
      }, { threshold: 0.45 });

      observer.observe(card);
    });
  });
};

export const initCharityInteractions = (scope = document) => {
  initCharityPurposeInteractions(scope);
  initCharityImpactInteractions(scope);
  initCharityInteractionHints(scope);
};

if (typeof document !== 'undefined') {
  initCharityInteractions();
}

if (typeof window !== 'undefined') {
  window.initCharityInteractions = initCharityInteractions;
}
